import { useState, type ReactNode } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { useFinancialsMutations } from "@/hooks/useFinancials";
import { formatDealValue, parseDealValueToDollars } from "@/lib/formatDealValue";
import { FIN_TEXT_CLASS } from "./chartColors";

/**
 * Star-time prompt: estimated deal value + fee %. Pre-filled from the prospect
 * when known. Estimates write to the company's EXISTING fee fields (no parallel
 * storage). Render only for granted users.
 *
 * Deliberately does NOT ask for an expected close month: a prospect has no
 * credible close date until it becomes a client, so starred prospects sit in
 * the timeline's "Unscheduled" column until someone sets the date on the client
 * detail page. Asking here would invite a made-up number.
 *
 * `children` is the ICON (not a button) — this component owns the <button>
 * in both states, so callers can't accidentally nest interactive elements.
 */
export function StarProspectPopover({
  prospect,
  starred,
  children,
}: {
  prospect: {
    id: string;
    name: string;
    estimatedDealValue?: string | number | null;
    expectedFeePercentage?: string | number | null;
  };
  starred: boolean;
  children: ReactNode;
}) {
  const { star, unstar } = useFinancialsMutations();
  const [open, setOpen] = useState(false);
  const [dealValue, setDealValue] = useState("");
  const [feePct, setFeePct] = useState("");

  const onOpenChange = (v: boolean) => {
    setOpen(v);
    if (v) {
      setDealValue(prospect.estimatedDealValue ? formatDealValue(String(prospect.estimatedDealValue)) : "");
      setFeePct(prospect.expectedFeePercentage ? String(Number(prospect.expectedFeePercentage)) : "");
    }
  };

  const submit = async () => {
    const edvParsed = dealValue.trim() ? Number(parseDealValueToDollars(dealValue)) : NaN;
    const pctParsed = feePct.trim() ? Number(feePct.replace(/[%\s]/g, "")) : NaN;
    try {
      await star.mutateAsync({
        companyId: prospect.id,
        estimatedDealValue: Number.isFinite(edvParsed) ? edvParsed : null,
        expectedFeePercentage: Number.isFinite(pctParsed) ? pctParsed : null,
      });
      toast.success(`${prospect.name} added to pipeline upside`);
      setOpen(false);
    } catch (e: any) {
      toast.error(e.message || "Failed to star prospect");
    }
  };

  if (starred) {
    return (
      <button
        type="button"
        title="Remove from financial dashboard"
        className={`${FIN_TEXT_CLASS.upside} hover:opacity-70`}
        onClick={() =>
          unstar.mutate(prospect.id, {
            onSuccess: () => toast.success(`${prospect.name} removed from upside`),
            onError: (e: any) => toast.error(e.message || "Failed to unstar"),
          })
        }
      >
        {children}
      </button>
    );
  }

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button type="button" title="Star for financial dashboard" className="text-muted-foreground hover:text-foreground">
          {children}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 space-y-3" align="end">
        <div>
          <p className="text-sm font-medium flex items-center gap-1.5">
            <Star className="w-3.5 h-3.5" /> Star {prospect.name}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Adds it to projected pipeline upside. Estimates are optional.
          </p>
        </div>
        <div className="grid gap-1.5">
          <Label className="text-xs">Estimated deal value</Label>
          <Input value={dealValue} onChange={(e) => setDealValue(e.target.value)} placeholder="e.g. $12M" />
        </div>
        <div className="grid gap-1.5">
          <Label className="text-xs">Expected fee %</Label>
          <Input value={feePct} onChange={(e) => setFeePct(e.target.value)} placeholder="e.g. 3" />
        </div>
        <Button size="sm" className="w-full" onClick={submit} disabled={star.isPending}>
          {star.isPending ? "Starring…" : "Star prospect"}
        </Button>
      </PopoverContent>
    </Popover>
  );
}
